import { useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { MaestroProvider, useMaestroAction, useMaestroCaption } from "@ae/maestrov2-form-lib";

// Headless "click → animate" for the Copilot STATS tiles.
//
// Instead of opening the Maestro modal, we mount a MaestroProvider with NO
// views (so no UI renders) and drive it via the same action pipeline the modal
// uses. The provider self-fetches the graphic definition from `graphicPath`,
// crawls its captions, and exposes `handleAction` via context — firing the
// caption's primary *_IN action does the exact BAKE→ANIMATION the operator's
// "Animate In" button does, straight to the renderer.
//
// `open={false}` keeps the provider passive: no auto-Default-on-open BAKE and
// no automatedNext preview loop run (both are gated on `open`), so nothing is
// sent to the renderer until we explicitly fire on click.

// Mirrors the library's (unexported) getPrimaryInAction: custom-action captions
// (MATCH_STATS_IN, CARD_IN, …) fire their suffixed *_IN; else the generic "IN".
const primaryInAction = (envelope) => {
  const actions = envelope?.actions || {};
  return Object.keys(actions).find((k) => k.endsWith("_IN") && k !== "IN") || "IN";
};

// The matching OUT action (MATCH_STATS_OUT / CARD_OUT / …); else generic "OUT".
const primaryOutAction = (envelope) => {
  const actions = envelope?.actions || {};
  return Object.keys(actions).find((k) => k.endsWith("_OUT") && k !== "OUT") || "OUT";
};

// Lives inside the provider so it can read the caption/action context slices.
// The provider is remounted per click (keyed by nonce), so each mount is a
// fresh "first mount" that selects `caption`; this bridge fires once the
// selection + caption crawl have completed.
function AnimateBridge({ caption, mode, onFired, sendErrorRef }) {
  const { handleAction } = useMaestroAction();
  const { selectedCaption, captions } = useMaestroCaption();
  const firedRef = useRef(false);

  useEffect(() => {
    if (!caption || firedRef.current) return;
    // Wait until the provider has selected this caption and fetched its
    // envelope (selection + crawl are async).
    const envelope = captions?.[caption];
    if (selectedCaption !== caption || !envelope) return;

    const action = mode === "OUT" ? primaryOutAction(envelope) : primaryInAction(envelope);
    firedRef.current = true;
    // The library's handleAction swallows broker-send errors (never rejects),
    // so we read the error the broker wrapper captured to report true success/failure.
    if (sendErrorRef) sendErrorRef.current = null;
    Promise.resolve(handleAction(action)).then(
      () => onFired?.(caption, mode, sendErrorRef?.current || null),
      (err) => onFired?.(caption, mode, sendErrorRef?.current || err),
    );
  }, [caption, mode, selectedCaption, captions, handleAction, onFired, sendErrorRef]);

  return null;
}

AnimateBridge.propTypes = {
  caption: PropTypes.string,
  mode: PropTypes.oneOf(["IN", "OUT"]),
  onFired: PropTypes.func,
  sendErrorRef: PropTypes.object,
};

export default function HeadlessStatsAnimator({
  graphicId,
  graphicPath,
  graphicName,
  brokerService,
  dataService,
  bindData,
  rendererId,
  target,
  initialFormData,
  onFired,
}) {
  const caption = target?.caption;
  const nonce = target?.nonce;
  const mode = target?.mode === "OUT" ? "OUT" : "IN";

  // handleAction (library) swallows broker-send failures, so wrap the broker to
  // capture the real send error — the bridge reads it to report true failure
  // instead of a false success toast.
  const sendErrorRef = useRef(null);
  const wrappedBroker = useMemo(() => {
    if (!brokerService?.send) return brokerService;
    return new Proxy(brokerService, {
      get(t, p) {
        if (p === "send") {
          return async (cmd) => {
            try {
              return await t.send(cmd);
            } catch (e) {
              sendErrorRef.current = e;
              throw e;
            }
          };
        }
        const v = t[p];
        return typeof v === "function" ? v.bind(t) : v;
      },
    });
  }, [brokerService]);

  // Nothing to animate yet, or missing wiring.
  if (!graphicPath || !brokerService || !caption || !nonce) return null;
  // key={nonce} remounts a fresh provider per click. A fresh mount is the only
  // trigger that selects initialCaption while open=false (see
  // shouldResetComposeState — non-mount triggers require open). open=false keeps
  // the provider passive: no auto-Default BAKE, no automatedNext preview loop.
  return (
    <MaestroProvider
      key={nonce}
      open={false}
      mount="inline"
      graphicId={graphicId}
      graphicPath={graphicPath}
      graphicName={graphicName}
      brokerService={wrappedBroker}
      dataService={dataService}
      bindData={bindData}
      rendererId={rendererId}
      initialCaption={caption}
      initialFormData={initialFormData}
    >
      <AnimateBridge caption={caption} mode={mode} onFired={onFired} sendErrorRef={sendErrorRef} />
    </MaestroProvider>
  );
}

HeadlessStatsAnimator.propTypes = {
  graphicId: PropTypes.string,
  graphicPath: PropTypes.string,
  graphicName: PropTypes.string,
  brokerService: PropTypes.object,
  dataService: PropTypes.object,
  bindData: PropTypes.func,
  rendererId: PropTypes.string,
  target: PropTypes.shape({ caption: PropTypes.string, nonce: PropTypes.number, mode: PropTypes.string }),
  initialFormData: PropTypes.object,
  onFired: PropTypes.func,
};
